import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';

import { User, UserList, ActivationRequest, UserAccount, PasswordResetRequest } from '../models/user.model';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';

@Injectable()
export class UserService {
    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) {
    }

    /**
     * Returns headers with token
     * @returns {HttpHeaders}
     */
    getAuthHeaders(): HttpHeaders {
        return this.authService.getHeadersWithToken();
    }

    /**
     * Creates and saves an admin user
     * @param {number} customerId
     * @param {User} user to create
     * @returns {Observable<User>}
     */
    createUser(customerId: number, user: User): Observable<User> {
        return this.http.put<User>(`${environment.apiBaseURL}/admin/customer/${customerId}/user`, user,
            { headers: this.getAuthHeaders() });
    }

    /**
     * Creates and saves a registered user
     * @returns {Observable<Object>}
     * @param body
     */
    createRegisteredUser(body): Observable<any> {
        return this.http.put<any>(`${environment.apiBaseURL}/register`, body);
    }

    /**
     * Activates a registered user
     * @param activationRequest
     * @returns {Observable<Object>}
     */
    activateRegisteredUser(activationRequest: ActivationRequest) {
        return this.http.post<ActivationRequest>(`${environment.apiBaseURL}/activate-account`, activationRequest);
    }

    /**
     * Get list of all users
     * @returns {Observable<UserList>}
     */
    getUsers(): Observable<UserList> {
        return this.http.get<UserList>(`${environment.apiBaseURL}/admin/user/list`,
            { headers: this.getAuthHeaders() });
    }

    /**
     * Gets user matching userId
     * @param {number} userId
     * @returns {Observable<UserList>}
     */
    getUser(userId: number): Observable<User> {
        return this.http.get<User>(`${environment.apiBaseURL}/admin/user/${userId}`,
            { headers: this.getAuthHeaders() });
    }

    /**
     * Get a user's account details
     * @param userId
     * @returns {Observable<Object>}
     */
    getUserAccount(userId: number): Observable<UserAccount> {
        return this.http.get<UserAccount>(`${environment.apiBaseURL}/user-account/${userId}`,
            { headers: this.getAuthHeaders() });
    }

    /**
     * Add Map Service to User Account
     * @param userId
     * @param mapServiceId
     * @returns {Observable<Object>}
     */
    addMapServiceToUserAccount(userId: number, mapServiceId: number): Observable<any> {
        return this.http.post<any>(`${environment.apiBaseURL}/user-account/${userId}/map-service/${mapServiceId}`, null,
            { headers: this.getAuthHeaders() });
    }

    /**
     * Request reset password for a User Account
     * @param email
     * @returns {Observable<Object>}
     */
    requestResetPasswordUserAccount(email: string): Observable<any> {
         return this.http.put<any>(`${environment.apiBaseURL}/user-account/${email.toLowerCase()}/password-reset`, null);
    }

    /**
     * Reset password for a User Account
     * @param email
     * @param newPassword
     * @param token
     * @returns {Observable<Object>}
     */
    resetPasswordUserAccount(passwordResetRequest: PasswordResetRequest): Observable<any> {
        let data = {
            newPassword: passwordResetRequest.newPassword,
            token: passwordResetRequest.token
        };
        return this.http.post<any>(`${environment.apiBaseURL}/user-account/${passwordResetRequest.email.toLowerCase()}/password-reset`, data);
    }

    /**
     * Updates the user
     * @param {number} userId
     * @param {User} user
     * @returns {Observable<User>}
     */
    updateUser(user: User): Observable<User> {
        return this.http.post<User>(`${environment.apiBaseURL}/admin/user/${user.id}`, user,
            { headers: this.getAuthHeaders() });
    }

     /**
     * Delete a user
     * @param userId
     * @returns {Observable<Object>}
     */
    deleteUser(userId: number) {
        return this.http.delete(`${environment.apiBaseURL}/admin/user/${userId}`, { headers: this.getAuthHeaders() });
    }
}
